import { relativeTime } from '../../lib/format.ts';
import type { TrashEntry } from '../../../shared/api.ts';

// Human "clears in" hint for a trashed deck: from its deletedAt + retention.
function clearsInLabel(deletedAt: number, retentionDays: number): string {
  const remainingMs = deletedAt + retentionDays * 86400000 - Date.now();
  if (remainingMs <= 0) return 'clears any moment now';
  const days = remainingMs / 86400000;
  if (days >= 1) return `clears in ${Math.ceil(days)} day${Math.ceil(days) === 1 ? '' : 's'}`;
  const hours = Math.ceil(remainingMs / 3600000);
  return `clears in ${hours} hour${hours === 1 ? '' : 's'}`;
}

/** The wire's `trash` list under the name the panel reads it by. */
export type TrashState = {
  entries: TrashEntry[];
  retentionDays: number;
  /** Whether the bin has actually been READ yet. `entries` is `[]` before the
   *  fetch answers, which is indistinguishable from an empty bin — and the
   *  dashboard bounces off an empty bin, so without this a `?status=trash`
   *  link bounced itself before the trash had loaded. */
  loaded: boolean;
};

type TrashListProps = {
  trash: TrashState;
  busy?: boolean;
  /** Multi-select over the trash list, owned by the parent (same pattern as
   *  the grid's selection): ids ticked, one toggle, and the bulk verbs. */
  selected: Set<string>;
  onToggle: (id: string) => void;
  onToggleAll: (all: boolean) => void;
  onRestoreSelected: () => void;
  onPurgeSelected: () => void;
  onRestore: (entry: TrashEntry) => void;
  onPurge: (entry: TrashEntry) => void;
  /** The raw input value — the parent clamps it to the 1–365 the field
   *  advertises, because a typed value can leave that range. */
  onChangeRetention: (days: string) => void;
};

/**
 * Trash panel — soft-deleted decks, newest first. Each can be restored to its
 * old slug or force-deleted now; the retention window (how long before the
 * sweep auto-removes them) is editable inline and persists to settings.
 */
export default function TrashList({
  trash,
  busy,
  selected,
  onToggle,
  onToggleAll,
  onRestoreSelected,
  onPurgeSelected,
  onRestore,
  onPurge,
  onChangeRetention,
}: TrashListProps) {
  const allSelected = trash.entries.length > 0 && selected.size === trash.entries.length;
  const someSelected = selected.size > 0 && !allSelected;
  return (
    <div className="trash">
      <div className="trash__head">
        <p className="muted small">
          Deleted decks are kept here, then permanently removed after{' '}
          <input
            type="number"
            min="1"
            max="365"
            className="trash__retention"
            defaultValue={trash.retentionDays}
            key={trash.retentionDays}
            disabled={busy}
            onBlur={(e) => onChangeRetention(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            aria-label="Days to keep deleted decks"
          />{' '}
          day{trash.retentionDays === 1 ? '' : 's'}. Restore anything before then.
        </p>
      </div>
      {/* Select-all + the bulk verbs. Always present (not hover-revealed) so
          "empty the trash" is one tick and one click, not a click per deck. */}
      <div className="trash__bulk">
        <label className="trash__selectall">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = someSelected;
            }}
            onChange={() => onToggleAll(!allSelected)}
            aria-label={allSelected ? 'Deselect all deleted decks' : 'Select all deleted decks'}
          />
          <span className="muted small">
            {selected.size > 0 ? `${selected.size} selected` : `Select all (${trash.entries.length})`}
          </span>
        </label>
        {selected.size > 0 && (
          <div className="trash__actions">
            <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={onRestoreSelected}>
              Restore {selected.size}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm trash__purge"
              disabled={busy}
              onClick={onPurgeSelected}
            >
              Delete {selected.size} permanently
            </button>
          </div>
        )}
      </div>
      <ul className="trash__list" role="list">
        {trash.entries.map((entry) => (
          <li key={entry.id} className={'trash__item' + (selected.has(entry.id) ? ' is-selected' : '')}>
            <input
              type="checkbox"
              className="trash__check"
              checked={selected.has(entry.id)}
              onChange={() => onToggle(entry.id)}
              aria-label={`Select ${entry.title}`}
            />
            <div className="trash__info">
              <span className="trash__title">{entry.title}</span>
              <span className="muted small">
                {entry.slug} · deleted {relativeTime(entry.deletedAt)} ·{' '}
                {clearsInLabel(entry.deletedAt, trash.retentionDays)}
              </span>
            </div>
            <div className="trash__actions">
              <button type="button" className="btn btn--ghost btn--sm" disabled={busy} onClick={() => onRestore(entry)}>
                Restore
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm trash__purge"
                disabled={busy}
                onClick={() => onPurge(entry)}
              >
                Delete permanently
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
